import { GridFilterInputValueProps } from '@mui/x-data-grid-premium'
import { SyntheticEvent } from 'react'
import * as React from 'react'
import { styled } from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import { ButtonBase, Checkbox, FormControlLabel } from '@mui/material'
import {
  Search,
  Close as CloseIcon,
  Done as DoneIcon,
} from '@mui/icons-material'

interface PopperComponentProps {
  anchorEl?: HTMLElement | unknown
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: 'var(--surface-0)',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: '1px solid  var(--line-200)',
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const { ...other } = props
  return <StyledAutocompletePopper {...other} />
}

const Button = styled(ButtonBase)(() => ({
  fontSize: 13,
  width: '100%',
  textAlign: 'left',
  paddingBottom: 8,
  color: 'var(--ink-500)',
  fontWeight: 600,
  '&:hover,&:focus': {
    color: 'var(--brand-blue-600)',
  },
  '& span': {
    width: '100%',
  },
  '& svg': {
    width: 16,
    height: 16,
  },
}))

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: '1px solid var(--line-300)',
  boxShadow: 'var(--shadow-pop)',
  borderRadius: 'var(--radius-md)',
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: 'var(--ink-900)',
  backgroundColor: 'var(--surface-0)',
}))

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 10,
  width: '100%',
  borderBottom: '1px solid var(--line-200)',
  '& input': {
    borderRadius: 4,
    backgroundColor: 'var(--surface-0)',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: '1px solid var(--line-200)',
    fontSize: 14,
    '&:focus': {
      boxShadow: '0 0 0 3px var(--brand-blue-50)',
      borderColor: 'var(--brand-blue-600)',
    },
  },
}))

export const AutoCompleteTextBox = (
  props: GridFilterInputValueProps & { data: string[]; label: string }
) => {
  const { data, item, applyValue, label } = props
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [pendingValue, setPendingValue] = React.useState<string[]>(
    item?.value ? Array.from(item.value) : data
  )

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }

  const handleCheckbox = (event: SyntheticEvent<Element, Event>) => {
    const { checked } = event.target
    if (checked) {
      setPendingValue([...data])
    } else {
      setPendingValue([])
    }
  }

  React.useEffect(() => {
    applyValue({
      ...item,
      value: new Set(pendingValue),
    })
  }, [pendingValue])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const open = Boolean(anchorEl)
  const id = open ? `supplyvue-search-${label}` : undefined

  return (
    <React.Fragment>
      <Box
        sx={{
          width: 150,
          fontSize: 20,
          height: 48,
          display: 'flex',
          alignItems: 'center',
        }}
        id={`#bu-${label}`}
      >
        <Button disableRipple aria-describedby={id} onClick={handleClick}>
          <span>{`Search ${
            label.length > 15 ? label.slice(0, 15) + '...' : label
          }`}</span>
          <Search fontSize="large" />
        </Button>
      </Box>
      <StyledPopper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: '1px solid var(--line-200)',
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              <FormControlLabel
                control={<Checkbox />}
                label="Select all"
                onChange={handleCheckbox}
                checked={data.length === item?.value?.size}
              />
            </Box>
            <Autocomplete
              open
              multiple
              disableCloseOnSelect
              clearOnBlur={false}
              onClose={(
                event: SyntheticEvent<Element, Event>,
                reason: AutocompleteCloseReason
              ) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              value={pendingValue}
              onChange={(event, newValue, reason) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return
                }
                setPendingValue(newValue)
              }}
              slots={{
                popper: PopperComponent,
              }}
              noOptionsText="No labels"
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Box
                    component={DoneIcon}
                    sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                    style={{
                      visibility: selected ? 'visible' : 'hidden',
                    }}
                  />
                  <Box
                    sx={{
                      flexGrow: 1,
                      '& span': {
                        color: 'var(--ink-500)',
                      },
                    }}
                  >
                    {option}
                  </Box>
                  <Box
                    component={CloseIcon}
                    sx={{ opacity: 0.6, width: 18, height: 18 }}
                    style={{
                      visibility: selected ? 'visible' : 'hidden',
                    }}
                  />
                </li>
              )}
              options={data}
              getOptionLabel={(option) => option}
              renderInput={(params) => {
                return (
                  <StyledInput
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    placeholder="Filter"
                  />
                )
              }}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </React.Fragment>
  )
}
